/**
 * 이용권 관리 컴포넌트 (DDD 리팩토링 버전)
 * UI 렌더링만 담당, 비즈니스 로직은 use-cases/services로 분리
 */

import React, { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  ClockIcon,
  CalendarDaysIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CogIcon,
  CheckCircleIcon,
  XMarkIcon,
  TagIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

import { usePricingData } from '../use-cases';
import { pricingService } from '../services';
import type { PricingPlan, PricingCreateRequest, PricingType, OperatingHours } from '../models';

const PricingManagement: React.FC = () => {
  // DDD 훅 사용
  const {
    pricingPlans,
    zones,
    zonePricing,
    operatingHours,
    loading,
    error,
    loadAllData,
    createPricingPlan,
    updatePricingPlan,
    deletePricingPlan,
    togglePlanActive,
    updateOperatingHours: updateOperatingHoursData,
  } = usePricingData();

  // 로컬 UI 상태
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showZonePricingModal, setShowZonePricingModal] = useState(false);
  const [showOperatingHoursModal, setShowOperatingHoursModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
  const [planForm, setPlanForm] = useState<PricingCreateRequest>({
    name: '',
    type: 'daily',  // 일수 기반으로 기본값 변경
    duration_hours: 720,  // 30일 (30 * 24)
    base_price: 50000,
    description: '',
    valid_days: undefined,
  });

  // 초기 데이터 로드
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // 폼 초기화
  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      type: 'daily',  // 일수 기반으로 기본값 변경
      duration_hours: 720,  // 30일 (30 * 24)
      base_price: 50000,
      description: '',
      valid_days: undefined,
    });
  };

  // 모달 열기
  const openPlanModal = (plan?: PricingPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        type: plan.type,
        duration_hours: plan.duration_hours,
        base_price: plan.base_price,
        description: plan.description || '',
        valid_days: plan.valid_days || undefined,
      });
    } else {
      setEditingPlan(null);
      resetPlanForm();
    }
    setShowPlanModal(true);
  };

  // 이용권 제출 핸들러
  const handlePlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let success: boolean;
    if (editingPlan) {
      success = await updatePricingPlan(editingPlan.id, {
        name: planForm.name,
        base_price: planForm.base_price,
        description: planForm.description,
        valid_days: planForm.valid_days || null,
      });
    } else {
      success = await createPricingPlan(planForm);
    }

    if (success) {
      setShowPlanModal(false);
      setEditingPlan(null);
      resetPlanForm();
    }
  };

  // 이용권 삭제 핸들러
  const handleDeletePlan = async (planId: string) => {
    await deletePricingPlan(planId);
  };

  // 이용권 활성화 토글 핸들러
  const handleTogglePlanActive = async (plan: PricingPlan) => {
    await togglePlanActive(plan);
  };

  // 운영시간 업데이트 핸들러
  const handleUpdateOperatingHours = async (newHours: OperatingHours[]) => {
    const success = await updateOperatingHoursData(newHours);
    if (success) {
      setShowOperatingHoursModal(false);
    }
  };

  // 타입에 따른 아이콘 가져오기
  const getPlanTypeIcon = (type: string) => {
    switch (type) {
      case 'hourly':
        return <ClockIcon className="h-5 w-5" />;
      case 'daily':
        return <CalendarDaysIcon className="h-5 w-5" />;
      case 'weekly':
        return <ChartBarIcon className="h-5 w-5" />;
      case 'monthly':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      default:
        return <TagIcon className="h-5 w-5" />;
    }
  };

  // 타입 변경 시 기본 기간 설정
  const handleTypeChange = (type: PricingType) => {
    setPlanForm({
      ...planForm,
      type,
      duration_hours: pricingService.getDefaultDuration(type),
    });
  };

  // 요일 관련 상수 및 헬퍼
  const DAY_NAMES = ['월', '화', '수', '목', '금', '토', '일'];
  const WEEKDAYS = [1, 2, 3, 4, 5];
  const WEEKENDS = [6, 7];
  const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

  const toggleDay = (day: number) => {
    const current = planForm.valid_days || [];
    const newDays = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day].sort();
    setPlanForm({ ...planForm, valid_days: newDays.length > 0 ? newDays : undefined });
  };

  const setQuickDays = (days: number[] | undefined) => {
    setPlanForm({ ...planForm, valid_days: days });
  };

  const getValidDaysLabel = (validDays?: number[]): string => {
    if (!validDays || validDays.length === 0 || validDays.length === 7) return '매일';
    const isWeekdays = validDays.length === 5 && WEEKDAYS.every(d => validDays.includes(d));
    const isWeekends = validDays.length === 2 && WEEKENDS.every(d => validDays.includes(d));
    if (isWeekdays) return '평일만';
    if (isWeekends) return '주말만';
    return validDays.map(d => DAY_NAMES[d - 1]).join(',');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XMarkIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button
            onClick={loadAllData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">이용권 관리</h1>
        <p className="text-gray-600 mt-1">이용권 가격, 구역별 요금, 운영시간을 관리합니다</p>
      </div>

      {/* 액션 버튼 */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => openPlanModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          이용권 추가
        </button>
        <button
          onClick={() => setShowZonePricingModal(true)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <CurrencyDollarIcon className="h-5 w-5" />
          구역별 요금 설정
        </button>
        <button
          onClick={() => setShowOperatingHoursModal(true)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <CogIcon className="h-5 w-5" />
          운영시간 설정
        </button>
      </div>

      {/* 이용권 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pricingPlans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-lg shadow-sm p-6 border-2 ${
              plan.is_active ? 'border-blue-500' : 'border-gray-200 opacity-60'
            }`}
          >
            {/* 이용권 헤더 */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  {getPlanTypeIcon(plan.type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      plan.type === 'hourly'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {plan.type === 'hourly' ? '시간권' : '정기권'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {pricingService.getPricingTypeLabel(plan.type)}
                    </span>
                  </div>
                </div>
              </div>
              {plan.is_active && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3" />
                  활성
                </span>
              )}
            </div>

            {/* 이용권 정보 */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">기간</span>
                <span className="font-medium">
                  {pricingService.formatDuration(plan.duration_hours, plan.type)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">가격</span>
                <span className="font-medium text-blue-600 text-lg">
                  {pricingService.formatPrice(plan.base_price)}
                </span>
              </div>
              {plan.valid_days && plan.valid_days.length > 0 && plan.valid_days.length < 7 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">이용 요일</span>
                  <span className="font-medium text-indigo-600">
                    {getValidDaysLabel(plan.valid_days)}
                  </span>
                </div>
              )}
              {plan.description && (
                <p className="text-xs text-gray-500 mt-2">{plan.description}</p>
              )}
            </div>

            {/* 액션 버튼 */}
            <div className="flex gap-2 pt-4 border-t">
              <button
                onClick={() => handleTogglePlanActive(plan)}
                className={`flex-1 px-3 py-2 text-sm rounded-lg ${
                  plan.is_active
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {plan.is_active ? '비활성화' : '활성화'}
              </button>
              <button
                onClick={() => openPlanModal(plan)}
                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDeletePlan(plan.id)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 이용권 추가/수정 모달 */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingPlan ? '이용권 수정' : '새 이용권 추가'}
            </h2>

            <form onSubmit={handlePlanSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이용권 이름 *</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 28일권"
                  required
                />
              </div>

              {!editingPlan && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">기간 (일) *</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={Math.floor(planForm.duration_hours / 24) || 1}
                        onChange={(e) => {
                          const days = parseInt(e.target.value) || 1;
                          setPlanForm({
                            ...planForm,
                            duration_hours: days * 24,
                            type: 'daily'  // 일수 기반은 daily 타입으로 고정
                          });
                        }}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        max="365"
                        required
                      />
                      <span className="text-gray-600">일</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">1~365일 사이의 숫자를 입력하세요</p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">가격 *</label>
                <input
                  type="number"
                  value={planForm.base_price}
                  onChange={(e) =>
                    setPlanForm({ ...planForm, base_price: parseInt(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="100"
                  required
                />
              </div>

              {/* 이용 가능 요일 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">이용 가능 요일</label>
                {/* 빠른 선택 버튼 */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setQuickDays(undefined)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      !planForm.valid_days || planForm.valid_days.length === 0
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    매일
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDays([...WEEKDAYS])}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      planForm.valid_days?.length === 5 && WEEKDAYS.every(d => planForm.valid_days?.includes(d))
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    평일만
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDays([...WEEKENDS])}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      planForm.valid_days?.length === 2 && WEEKENDS.every(d => planForm.valid_days?.includes(d))
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    주말만
                  </button>
                </div>
                {/* 요일 개별 선택 */}
                <div className="flex gap-2">
                  {DAY_NAMES.map((name, idx) => {
                    const day = idx + 1;
                    const isSelected = !planForm.valid_days || planForm.valid_days.length === 0 || planForm.valid_days.includes(day);
                    const isWeekend = day >= 6;
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                          isSelected
                            ? isWeekend
                              ? 'bg-red-500 text-white'
                              : 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  선택하지 않으면 매일 이용 가능합니다
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                <textarea
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="이용권에 대한 간단한 설명"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPlan ? '수정' : '추가'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPlanModal(false);
                    setEditingPlan(null);
                    resetPlanForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 구역별 요금 모달 */}
      {showZonePricingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">구역별 요금 설정</h2>
              <button
                onClick={() => setShowZonePricingModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {zones.map((zone) => (
                <div key={zone.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">{zone.name}</p>
                    <p className="text-sm text-gray-500">프리미엄 배율: {zone.premium_rate}x</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowZonePricingModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 운영시간 모달 */}
      {showOperatingHoursModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">운영시간 설정</h2>
              <button
                onClick={() => setShowOperatingHoursModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {operatingHours.map((hour) => (
                <div key={hour.day_of_week} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{pricingService.getDayName(hour.day_of_week)}</p>
                  </div>
                  {!hour.is_closed && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{pricingService.formatTime(hour.open_time)}</span>
                      <span className="text-gray-400">~</span>
                      <span className="text-sm">{pricingService.formatTime(hour.close_time)}</span>
                    </div>
                  )}
                  {hour.is_closed && <span className="text-sm text-gray-500">휴무</span>}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowOperatingHoursModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingManagement;
